import type { ApolloError } from '@apollo/client';
import type {
  EmployeeExportFormat,
  EmployeeExportHistory,
  ExportHistoryFieldsFragment,
  ExportProgressFieldsFragment,
  PerformanceReviewExportHistory,
  PerformanceReviewExportType,
} from '@/common/graphql';

/** Which export domain a given `ExportHistoryList` instance renders. */
export type ExportHistoryKind =
  | 'employee'
  | 'numberOfEmployee'
  | 'performanceReviewList'
  | 'performanceReviewResults'
  | 'externalAccount'
  | 'probationReview';

/**
 * Common projection shared by every export kind's `exportHistoryList` row. The base fields are
 * derived via `Pick` over the generated `ExportHistoryFields` fragment (a discriminated union in
 * codegen output — `Pick`'s indexed access flattens it to one plain object type here, since every
 * kind selects the exact same base fields) rather than hand-duplicated. `format`/`exportType` are
 * added as plain optional fields (not a discriminated union) so the existing base+specialize
 * mapper composition (`toExportHistoryRow` → `toEmployeeExportHistoryRow`/`toPerformanceReviewExportHistoryRow`)
 * can spread-and-extend a value without cross-kind branch mismatches.
 */
export type ExportHistoryRowData = Pick<
  ExportHistoryFieldsFragment,
  | 'id'
  | 'status'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'totalRequestCount'
  | 'completedRequestCount'
  | 'resultUrl'
  | 'resultFileName'
  | 'failureReason'
> &
  Partial<Pick<EmployeeExportHistory, 'format'> & Pick<PerformanceReviewExportHistory, 'exportType'>>;

/** Input shape for [toExportHistoryRow] — the generated `ExportHistoryFields` fragment shared by every export kind. */
type ExportHistoryRowInput = ExportHistoryFieldsFragment;

/** Normalizes a raw export-history GraphQL item's fields to `ExportHistoryRowData`. */
export const toExportHistoryRow = (item: ExportHistoryRowInput): ExportHistoryRowData => ({
  id: item.id,
  status: item.status,
  createdAt: item.createdAt,
  createdBy: item.createdBy,
  updatedAt: item.updatedAt,
  totalRequestCount: item.totalRequestCount,
  completedRequestCount: item.completedRequestCount,
  resultUrl: item.resultUrl,
  resultFileName: item.resultFileName,
  failureReason: item.failureReason,
});

/** Employee-kind row mapper — adds the Employee-only `format` field on top of the shared base. */
export const toEmployeeExportHistoryRow = (
  item: ExportHistoryRowInput & { format: EmployeeExportFormat },
): ExportHistoryRowData => ({ ...toExportHistoryRow(item), format: item.format });

/**
 * Performance-Review-kind row mapper — adds the Performance-Review-only `exportType` field on top
 * of the shared base.
 */
export const toPerformanceReviewExportHistoryRow = (
  item: ExportHistoryRowInput & { exportType: PerformanceReviewExportType },
): ExportHistoryRowData => ({ ...toExportHistoryRow(item), exportType: item.exportType });

/**
 * Common projection shared by all 6 kinds' Progress subscription payloads — the generated
 * `ExportProgressFields` fragment, narrowed to just the fields the live-progress merge uses
 * (drops `createdBy`, which isn't rendered for progress rows).
 */
export type ExportProgressItem = Pick<
  ExportProgressFieldsFragment,
  'id' | 'status' | 'createdAt' | 'updatedAt' | 'totalRequestCount' | 'completedRequestCount'
>;

/** Shape returned by each per-kind `use<Kind>ExportHistory` hook. */
export interface UseExportHistoryResult {
  error: ApolloError | undefined;
  history: Array<ExportHistoryRowData>;
  isActive: boolean;
  loading: boolean;
  progress: Array<ExportProgressItem>;
  refetch: () => void;
}
